'use client';

import { useMemo, useState } from 'react';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/use-locale';
import { CompanySearchResult } from '@/lib/actions/company-search';
import { ExportDialog } from './ResultsExportDialog';
import { buildResultColumns } from './resultsTableColumnDefs';
import { InsufficientCreditsDialog } from '@/new_components/shared/InsufficientCreditsDialog';
import { CreditsUsedPopup } from '@/new_components/shared/CreditsUsedPopup';
import { WEB_CREDIT_COSTS } from '@/config/credit-costs.config';
import type { SearchExportOutcome } from '@/lib/search-export';
import { SignupRequiredDialog } from '@/new_components/shared/SignupRequiredDialog';
import { ResultsToolbar } from './ResultsToolbar';
import { ResultsTableBody } from './ResultsTableBody';
import { useColumnVisibility, useSignupDialog, useResultSlices, useResultsEnrich, useResultsExport, useSortToggle } from './useResultsTableActions';

const SIGNUP_DESCRIPTION_KEY = {
  export: 'search.accountRequired.descriptionExport',
  columns: 'search.accountRequired.descriptionColumns',
  enrich: 'search.accountRequired.descriptionEnrich',
} as const;

interface CompanyResultsTableProps {
  previewEuId?: string | null;
  onOpenPreview?: (euId: string, trigger?: HTMLElement) => void;
  results: CompanySearchResult[];
  total: number;
  totalCapped?: boolean;
  offset: number;
  limit?: number;
  sort?: string;
  financialYear?: number;
  // Whether the user may export (any signed-in user). The export button is
  // shown to everyone; guests get the "account required" prompt instead.
  canExport?: boolean;
  // Whether the user may run the enrich (website/employee) batch actions —
  // any signed-in user; guests get the "account required" prompt.
  canEnrich?: boolean;
  // Whether the user may customize visible columns (any signed-in user). The
  // Columns button is shown to everyone; guests get the "account required" prompt.
  canCustomizeColumns?: boolean;
  // Fetches up to `rows` rows for export using the active filters/sort. The
  // backend bills the export (WEB_CREDIT_COSTS.excelExport) when it hands
  // over the rows. Without it the export dialog cannot export.
  fetchResultsForExport?: (rows: number) => Promise<SearchExportOutcome>;
  // Called after an employee enrich with the fresh per-company counts so the
  // parent can overlay them on the displayed rows (mv_search can't be written
  // per-click). Keyed by euId → latest-year total.
  onEmployeeCountsUpdated?: (counts: Record<string, number>) => void;
  isLoading?: boolean;
  onSortChange?: (sort: string) => void;
  onOffsetChange?: (offset: number) => void;
  // Change the page size (results per page). Wired to the results-per-page
  // dropdown in the bottom pagination bar.
  onLimitChange?: (limit: number) => void;
  onRefresh?: () => void;
  // Fired when the export button is clicked — for every user (incl. guests and
  // non-entitled users), before the sign-in check. Used for usage analytics.
  onExportClick?: () => void;
  // Fired after an export file is generated successfully, with what was
  // exported so the parent can log it alongside the active filters.
  onExportSuccess?: (info: { exportCount: number; columns: string[]; charged: number }) => void;
  // The search's country scope ('DE' | 'CH' | 'AT'; undefined = global). Swiss
  // companies carry no financials, industry or contact data, so 'CH' swaps
  // in a default column set built from what the Swiss register does have.
  country?: string;
}

export default function CompanyResultsTable({
  results,
  total,
  totalCapped = false,
  offset,
  limit = 50,
  sort,
  financialYear,
  canExport = false,
  canEnrich = false,
  canCustomizeColumns = false,
  fetchResultsForExport,
  onEmployeeCountsUpdated,
  isLoading = false,
  onSortChange,
  onOffsetChange,
  onLimitChange,
  onRefresh,
  onExportClick,
  onExportSuccess,
  country,
  previewEuId,
  onOpenPreview,
}: CompanyResultsTableProps) {
  const { t, i18n } = useTranslation();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);
  const totalLabel =
    fmt.fullNumber.format(total) + (totalCapped ? t('search.totalCappedSuffix') : '');
  const { locale } = useLocale();
  const { showSignupDialog, setShowSignupDialog, signupDialogFeature, openSignupDialog } = useSignupDialog();
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const {
    showExportDialog, setShowExportDialog, isExporting, exportError, handleExportClick, runExport,
    creditsUsed, clearCreditsUsed,
  } = useResultsExport({
    t, financialYear, country, canExport, fetchResultsForExport, onExportClick,
    onExportSuccess, openSignupDialog, onInsufficientCredits: () => setShowCreditsDialog(true),
  });
  // Row selection, keyed by eu_id so it survives paging and re-sorting.
  // Deliberately NOT cleared when the page changes: picking three companies
  // on page 1 and two on page 2 before adding them to a list is the whole
  // point of selecting across a result set.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedEuIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );
  const { columnVisibility, handleColumnVisibilityChange } = useColumnVisibility(country, canCustomizeColumns);
  const handleSort = useSortToggle(onSortChange, sort);
  const { isEnrichingWebsites, isEnrichingEmployees, handleEnrichWebsites, handleEnrichEmployees } =
    useResultsEnrich({ results, canEnrich, openSignupDialog, onRefresh, onEmployeeCountsUpdated });

  // A new column goes in resultsTableColumnDefs.tsx; this memo's deps are what the renderers read.
  const columns = useMemo<ColumnDef<CompanySearchResult>[]>(
    () => buildResultColumns({ t, language: i18n.language, fmt, offset, sort, handleSort, country, locale, results, onOpenPreview }),
    [offset, sort, t, i18n.language, fmt, handleSort, country, locale, results, onOpenPreview],
  );

  const { displayedResults, mobileResults } = useResultSlices(results, limit);

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-background lg:overflow-hidden">
      {/* Toolbar (columns / export / enrich) is desktop-only — hidden on mobile
          so the export and enrichment actions don't show there. */}
      <ResultsToolbar
        t={t}
        country={country}
        columnVisibility={columnVisibility}
        handleColumnVisibilityChange={handleColumnVisibilityChange}
        canCustomizeColumns={canCustomizeColumns}
        openSignupDialog={openSignupDialog}
        selectedEuIds={selectedEuIds}
        displayedResults={displayedResults}
        setRowSelection={setRowSelection}
        handleExportClick={handleExportClick}
        results={results}
        handleEnrichWebsites={handleEnrichWebsites}
        isEnrichingWebsites={isEnrichingWebsites}
        handleEnrichEmployees={handleEnrichEmployees}
        isEnrichingEmployees={isEnrichingEmployees}
      />

      <div className="flex-1 min-h-0 min-w-0 relative lg:overflow-hidden">
        <ResultsTableBody
          t={t} isLoading={isLoading} previewEuId={previewEuId} onOpenPreview={onOpenPreview}
          displayedResults={displayedResults} mobileResults={mobileResults} columns={columns}
          columnVisibility={columnVisibility}
          handleColumnVisibilityChange={handleColumnVisibilityChange} total={total}
          totalLabel={totalLabel} locale={locale} offset={offset} limit={limit}
          onOffsetChange={onOffsetChange} onLimitChange={onLimitChange}
          rowSelection={rowSelection} setRowSelection={setRowSelection}
        />
      </div>

      <SignupRequiredDialog
        open={showSignupDialog}
        onOpenChange={setShowSignupDialog}
        source={`search_${signupDialogFeature}`}
        description={t(SIGNUP_DESCRIPTION_KEY[signupDialogFeature])}
      />
      <InsufficientCreditsDialog
        open={showCreditsDialog}
        onOpenChange={setShowCreditsDialog}
        required={WEB_CREDIT_COSTS.excelExport}
      />
      <CreditsUsedPopup snapshot={creditsUsed} onClose={clearCreditsUsed} />

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        total={total}
        totalCapped={totalCapped}
        totalLabel={totalLabel}
        t={t}
        isExporting={isExporting}
        exportError={exportError}
        onExport={runExport}
        country={country}
      />
    </div>
  );
}
